import { useMemo, useState } from "react";
import CalculatorShell, { Field, Result, inputClass } from "@/components/CalculatorShell";
import { getCalculator } from "@/data/calculators";
import {
  regularHolidayEntitlement,
  irregularHoursAccrual,
  rolledUpHolidayPay,
  leaverHoliday,
  gbp,
} from "@/lib/employment-calcs";

const calc = getCalculator("holiday-entitlement-calculator");

const MODES = [
  { id: "regular", label: "Regular or part-time hours" },
  { id: "irregular", label: "Irregular hours or part-year (12.07%)" },
  { id: "leaver", label: "Leaving: holiday owed" },
];

const fmt = (n) => Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 });

function Regular() {
  const [days, setDays] = useState("5");
  const [hours, setHours] = useState("");
  const r = useMemo(() => (days ? regularHolidayEntitlement({ daysPerWeek: days, hoursPerWeek: hours }) : null), [days, hours]);
  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Days worked each week" htmlFor="days">
          <input id="days" type="number" min="0.5" max="7" step="0.5" inputMode="decimal" className={inputClass} value={days} onChange={(e) => setDays(e.target.value)} data-testid="hol-days" />
        </Field>
        <Field label="Hours worked each week" hint="Optional, to see the entitlement in hours." htmlFor="hours">
          <input id="hours" type="number" min="0" step="0.25" inputMode="decimal" className={inputClass} value={hours} onChange={(e) => setHours(e.target.value)} data-testid="hol-hours" />
        </Field>
      </div>
      {r?.error && <p className="mt-6 text-sm font-semibold text-signal">{r.error}</p>}
      {r && !r.error && (
        <Result
          items={[
            { label: "Statutory leave a year", value: `${fmt(r.entitlementDays)} days`, big: true, sub: r.capped ? "5.6 weeks, capped at 28 days" : `${fmt(days)} days × 5.6 weeks` },
            ...(r.entitlementHours ? [{ label: "In hours", value: `${fmt(r.entitlementHours)} hours` }] : []),
          ]}
          notes={[
            "Bank holidays can count towards this unless the contract says they are extra.",
            "A contract can give more, never less. Pay for the first 4 weeks must include regular overtime and commission.",
          ]}
        />
      )}
    </>
  );
}

function Irregular() {
  const [hoursText, setHoursText] = useState("");
  const [pay, setPay] = useState("");
  const periods = hoursText.split(/[\s,;]+/).filter(Boolean);
  const r = useMemo(() => (periods.length ? irregularHoursAccrual(periods) : null), [hoursText]); // eslint-disable-line react-hooks/exhaustive-deps
  const rolled = pay !== "" ? rolledUpHolidayPay(pay) : null;
  return (
    <>
      <div className="grid gap-6">
        <Field
          label="Hours worked in each pay period"
          hint="One figure per pay period (week or month), separated by commas or new lines. Each period is rounded separately, as the regulations require."
          htmlFor="periods"
        >
          <textarea id="periods" rows={3} className={inputClass} placeholder="e.g. 68, 42.5, 0, 75" value={hoursText} onChange={(e) => setHoursText(e.target.value)} data-testid="hol-periods" />
        </Field>
        <Field label="Pay for one pay period (£), for rolled-up holiday pay" hint="Optional. Normal pay for the period, including regular overtime and commission." htmlFor="rpay">
          <input id="rpay" type="number" min="0" step="0.01" inputMode="decimal" className={inputClass} value={pay} onChange={(e) => setPay(e.target.value)} data-testid="hol-rolled-pay" />
        </Field>
      </div>
      {(r || rolled != null) && (
        <Result
          items={[
            ...(r ? [
              { label: "Holiday built up", value: `${r.totalAccrued} hours`, big: true, sub: `from ${fmt(r.totalWorked)} hours over ${r.rows.length} pay period${r.rows.length === 1 ? "" : "s"}` },
              { label: "Per period", value: r.rows.map((x) => x.accrued).join(", "), sub: "hours, after rounding" },
            ] : []),
            ...(rolled != null ? [{ label: "Rolled-up holiday pay", value: gbp(rolled), sub: "12.07% of that period's pay" }] : []),
          ]}
          notes={[
            "Applies to leave years starting on or after 1 April 2024, in Great Britain.",
            "Under 30 minutes in a period is ignored; 30 minutes or more counts as a full hour (reg 15B(5)).",
            "No more than 28 days can build up this way in a leave year.",
            "During sickness or family leave no hours are worked, so nothing builds up under reg 15B. Leave for those periods is worked out under reg 15C from the worker's own past hours, which this calculator does not do.",
            ...(rolled != null ? [
              "Rolled-up holiday pay must be paid with the wages for the same period and shown as a separate payslip line (reg 16A).",
              "For sickness, maternity and other statutory leave, rolled-up pay is not 12.07% of that period's pay. It is based on the average rolled-up pay in earlier periods (reg 16A).",
            ] : []),
          ]}
        />
      )}
    </>
  );
}

function Leaver() {
  const [form, setForm] = useState({ daysPerWeek: "5", contractualDays: "", leaveYearStart: "", startDate: "", endDate: "", daysTaken: "", carriedOver: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const r = useMemo(() => (form.leaveYearStart && form.endDate ? leaverHoliday(form) : null), [form]);
  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Days worked each week" htmlFor="ldays">
          <input id="ldays" type="number" min="0.5" max="7" step="0.5" inputMode="decimal" className={inputClass} value={form.daysPerWeek} onChange={set("daysPerWeek")} data-testid="hol-leaver-days" />
        </Field>
        <Field label="Full-year entitlement under the contract, in days" hint="Optional. Leave blank if the contract only gives the statutory minimum." htmlFor="annual">
          <input id="annual" type="number" min="0" step="0.5" inputMode="decimal" className={inputClass} value={form.contractualDays} onChange={set("contractualDays")} data-testid="hol-annual" />
        </Field>
        <Field label="Leave year starts on" htmlFor="ly">
          <input id="ly" type="date" className={inputClass} value={form.leaveYearStart} onChange={set("leaveYearStart")} data-testid="hol-leave-year" />
        </Field>
        <Field label="Start date, if they joined this leave year" htmlFor="pstart">
          <input id="pstart" type="date" className={inputClass} value={form.startDate} onChange={set("startDate")} data-testid="hol-start" />
        </Field>
        <Field label="Last day of employment" htmlFor="pend">
          <input id="pend" type="date" className={inputClass} value={form.endDate} onChange={set("endDate")} data-testid="hol-end" />
        </Field>
        <Field label="Days already taken this leave year" htmlFor="taken">
          <input id="taken" type="number" min="0" step="0.5" inputMode="decimal" className={inputClass} value={form.daysTaken} onChange={set("daysTaken")} data-testid="hol-taken" />
        </Field>
        <Field label="Untaken days carried over from earlier years" hint="Optional. Include leave that could not be taken because of sickness or family leave." htmlFor="carried">
          <input id="carried" type="number" min="0" step="0.5" inputMode="decimal" className={inputClass} value={form.carriedOver} onChange={set("carriedOver")} data-testid="hol-carried" />
        </Field>
      </div>
      {r?.error && <p className="mt-6 text-sm font-semibold text-signal">{r.error}</p>}
      {r && !r.error && (
        <Result
          items={[
            { label: "Statutory leave to pay for", value: `${fmt(r.statutoryOwed)} days`, big: true, sub: `${fmt(r.statutoryDue)} earned${r.carried ? ` + ${fmt(r.carried)} carried over` : ""} − ${fmt(r.taken)} taken` },
            ...(r.contractualExtraDue > 0 ? [{ label: "Extra contractual leave owed", value: `${fmt(r.contractualOwed)} days`, sub: "paid as the contract says" }] : []),
            ...(r.overtaken > 0 ? [{ label: "Leave taken in excess", value: `${fmt(r.overtaken)} days`, sub: "recoverable only under a prior written term" }] : []),
            { label: "Share of the leave year", value: `${fmt(r.fraction * 100)}%`, sub: `${fmt(r.fraction * r.yearDays)} of ${r.yearDays} days` },
          ]}
          notes={[
            "Leave taken this year is set against statutory leave first. Untaken statutory leave must be paid for at a rate no lower than the statutory formula, whatever the contract says (reg 14).",
            "The contract can only set the rate for leave above the statutory minimum. You can recover leave taken in excess only if a written term agreed before it was taken allows the deduction.",
            "The first-year limit on how much leave can be taken (reg 15A) does not reduce what is paid on leaving, so it is not used here.",
            "Leave built up during sickness or family leave is included in carried-over days. This calculator does not work out holiday pay rates, which use the 52-week average (reg 16).",
          ]}
        />
      )}
    </>
  );
}

export default function HolidayCalculator() {
  const [mode, setMode] = useState("regular");
  return (
    <CalculatorShell
      calc={calc}
      howItWorks={
        <>
          <p>
            <strong>Regular and part-time workers</strong> get 5.6 weeks' paid leave a year: 4 weeks under reg 13 and 1.6
            weeks under reg 13A of the Working Time Regulations 1998, capped at 28 days. A five-day week gives 28 days, a
            three-day week 16.8 days.
          </p>
          <p>
            <strong>Irregular-hours and part-year workers</strong> (leave years starting on or after 1 April 2024) build up
            leave at <strong>12.07%</strong> of the hours they actually work in each pay period (reg 15B). 12.07% is 5.6
            weeks of holiday divided by the 46.4 weeks a year a full-time worker is at work. Who counts is set out in reg 15F:
            paid hours that are wholly or mostly variable, or a contract that only requires work for part of the year with
            unpaid weeks off.
          </p>
          <p>
            For those workers only, you may pay <strong>rolled-up holiday pay</strong>: 12.07% on top of each period's pay,
            shown separately on the payslip (reg 16A).
          </p>
          <p>
            <strong>Leavers</strong> are paid for the share of the year's statutory leave they have earned and not taken,
            plus leave carried over (reg 14). The contract cannot reduce that payment. In the first year, reg 15A limits how
            much leave can be <em>taken</em> each month, but it does not reduce the payment on leaving.
          </p>
          <p>
            <strong>Converting days into hours</strong> for a regular worker uses GOV.UK's practical method: the average
            working day multiplied by the days of leave. The regulations themselves speak in weeks.
          </p>
          <p>
            <strong>Holiday pay</strong> is based on the average of the last 52 paid weeks, including regular overtime and
            commission for the first 4 weeks (reg 16). Employers must keep records showing compliance with the holiday rules
            for six years, and failing to do so is already a criminal offence (reg 16B, in force 6 April 2026). The Fair Work
            Agency takes over enforcement in 2027, but the duty applies now.
          </p>
        </>
      }
      example={
        <p>
          Sam works zero hours on a monthly payroll. In March she works 68 hours: 68 × 12.07% = 8.21, which rounds to{" "}
          <strong>8 hours</strong> of holiday. If you use rolled-up holiday pay and her March pay is £860, you add £103.80
          as a separate payslip line.
        </p>
      }
    >
      <div role="tablist" aria-label="Type of worker" className="flex flex-wrap gap-2">
        {MODES.map((m) => (
          <button
            key={m.id}
            role="tab"
            type="button"
            aria-selected={mode === m.id}
            onClick={() => setMode(m.id)}
            data-testid={`hol-mode-${m.id}`}
            className={`border-2 px-4 py-2 text-sm font-bold transition-colors ${mode === m.id ? "border-ink bg-ink text-paper" : "border-ink/20 text-ink hover:border-ink"}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {mode === "regular" && <Regular />}
        {mode === "irregular" && <Irregular />}
        {mode === "leaver" && <Leaver />}
      </div>
    </CalculatorShell>
  );
}
